import { gql } from "@apollo/client";
import { batch } from "react-redux";
import { captureException } from "@sentry/browser";

import config from "@/config/config";
import { getRefillPaymentLevel } from "@/helpers/payment";
import sleep from "@/helpers/sleep";
import type { AppState } from "@/reducers";
import Tracker from "@/services/Tracker";
import { renderCurrencyFromCentimes } from "@/helpers/currency";

import {
  createStripeCustomer,
  getCustomerProfile,
  getEdenredUsername,
  getPaymentCards,
} from "./account";
import { refreshUserFromToken } from "./auth";
import { formHandleInput } from "./form";
import { apollo, AppDispatch, getTokenFromCookie } from "./index";
import {
  WALLET_AMOUNT_SET,
  WALLET_AMOUNT_ERROR,
  ACCOUNT_ADD_CARD_SUCCESS,
  ACCOUNT_ADD_CARD_ERROR,
  WALLET_DEFAULTPAYMENTMETHOD_SET,
  WALLET_DEFAULTPAYMENTMETHOD_ERROR,
  WALLET_REFILL_LOADING,
  WALLET_REFILL_ERROR,
  WALLET_REFILL_SUCCESS,
  WALLET_ONBOARDING_LOADING,
  WALLET_ONBOARDING_ERROR,
  WALLET_ONBOARDING_SUCCESS,
  WALLET_REFILL_RESET,
  WALLET_EDENREDAUTHCALLBACK_SUCCESS,
  WALLET_PAYZENFORMTOKEN_SET,
  APP_LOADING_START,
  APP_LOADING_END,
  WALLET_PAYZENFORMTOKEN_LOADING,
} from "./types";

import * as RootNavigation from "@/navigators/RootNavigatorRef";
import CookieManager from "@/services/CookieManager";
import { addToast } from "./toast";
import type { TranslateOptions } from "@/i18n";
import { translateMessage } from "@/helpers/translation";

const translateWalletMessage = (key: string, options?: TranslateOptions) =>
  translateMessage(`wallet.${key}`, options);

const getWalletMessages = () => ({
  oopsTitle: translateWalletMessage("titles.oops", { defaultValue: "Oups !" }),
  successTitle: translateWalletMessage("titles.success", { defaultValue: "Félicitations !" }),
  sessionExpired: translateWalletMessage("errors.sessionExpired", {
    defaultValue: "Votre session a expiré. Veuillez vous reconnecter.",
  }),
  technicalError: translateWalletMessage("errors.technical", {
    defaultValue: "Une erreur technique est survenue, vous n'avez pas été débité",
  }),
  invalidCard: translateWalletMessage("errors.invalidCard", {
    defaultValue:
      "Il semblerait que votre carte ne soit pas valide, veuillez verifier les informations saisies",
  }),
  walletCredited: translateWalletMessage("success.walletCredited", {
    defaultValue: "Votre compte a bien été crédité",
  }),
  cardAdded: translateWalletMessage("success.cardAdded", {
    defaultValue: "Votre carte a bien été ajoutée",
  }),
  incorrectAmount: (min: string, max: string) =>
    translateWalletMessage("errors.incorrectAmount", {
      defaultValue: "Montant invalide (min {{min}}, max {{max}})",
      params: { min, max },
    }),
  payzenDailyLimit: translateWalletMessage("errors.payzenDailyLimit", {
    defaultValue:
      "Une erreur est survenue, veuillez vérifier que vous n'avez pas atteint votre plafond journalier.",
  }),
  edenred: {
    linkError: translateWalletMessage("edenred.linkError", {
      defaultValue:
        "Une erreur technique est survenue lors de la liaison avec Edenred, veuillez réessayer",
    }),
    linked: translateWalletMessage("edenred.linked", {
      defaultValue: "Votre compte Edenred a bien été liée à votre compte Popchef",
    }),
    communicationError: translateWalletMessage("edenred.communicationError", {
      defaultValue:
        "Une erreur est survenue lors de la communication avec Edenred, veuillez réessayer.",
    }),
  },
});

export const addCard =
  (stripeHoc: any): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getWalletMessages();
    dispatch({ type: APP_LOADING_START });
    const {
      account: { profile },
    } = getState();

    // Vérifier la session avant tout appel GraphQL
    const authToken = await getTokenFromCookie();
    if (!authToken) {
      dispatch(
        addToast({
          type: "error",
          message: messages.sessionExpired,
        }),
      );
      dispatch({ type: APP_LOADING_END });
      return;
    }

    const createPaymentMethod = await stripeHoc.createPaymentMethod("card");

    // Check if user already have a stripeCustomerId
    if (!profile.stripeCustomerId) {
      await dispatch(createStripeCustomer());
    }

    if (createPaymentMethod.error) {
      dispatch({
        type: ACCOUNT_ADD_CARD_ERROR,
        errorMessage: createPaymentMethod.error.message,
      });
      dispatch({ type: APP_LOADING_END });
      return;
    }

    let setupIntentSecret;

    const toSetAsDefaultPaymentMethod = true;

    try {
      const req: any = await apollo().mutate<any>({
        variables: { toSetAsDefaultPaymentMethod },
        mutation: gql`
          mutation createSesameSetupIntent($toSetAsDefaultPaymentMethod: Boolean!) {
            createSesameSetupIntent(toSetAsDefaultPaymentMethod: $toSetAsDefaultPaymentMethod)
          }
        `,
      });

      const graphQLErrors = req?.errors;
      if (graphQLErrors && graphQLErrors.length > 0) {
        const firstMessage = String(graphQLErrors[0]?.message || "GraphQL error");
        if (process.env.NODE_ENV !== "production") {
          console.error("createSesameSetupIntent GraphQL errors", graphQLErrors);
        }
        dispatch(
          addToast({
            type: "error",
            message:
              messages.technicalError +
              (process.env.NODE_ENV !== "production" && firstMessage ? `: ${firstMessage}` : ""),
          }),
        );
        dispatch({ type: APP_LOADING_END });
        return;
      }

      const secretCandidate = req?.data?.createSesameSetupIntent;
      if (!secretCandidate) {
        dispatch(
          addToast({
            type: "error",
            message: messages.technicalError,
          }),
        );
        dispatch({ type: APP_LOADING_END });
        return;
      } else {
        setupIntentSecret = secretCandidate;
      }
    } catch (err: any) {
      captureException(err);
      if (process.env.NODE_ENV !== "production") {
        // Aide au debug en environnement de développement

        console.error("createSesameSetupIntent error", err);
      }
      dispatch(
        addToast({
          type: "error",
          message:
            messages.technicalError +
            (process.env.NODE_ENV !== "production" && err?.message
              ? `: ${String(err.message)}`
              : ""),
        }),
      );
      dispatch({
        type: ACCOUNT_ADD_CARD_ERROR,
        errorMessage: messages.technicalError,
      });
      dispatch({ type: APP_LOADING_END });
      return;
    }
    try {
      const confirmSetupIntentResult = await stripeHoc.handleCardSetup(setupIntentSecret);

      if (confirmSetupIntentResult.error) {
        dispatch(
          addToast({
            type: "error",
            title: messages.oopsTitle,
            message: messages.invalidCard,
          }),
        );
        await dispatch({
          type: ACCOUNT_ADD_CARD_ERROR,
          errorMessage: confirmSetupIntentResult.error.message,
        });
        dispatch({ type: APP_LOADING_END });

        try {
          await Tracker.track("Payment Method Added Failed", {
            source: "Stripe",
            error: confirmSetupIntentResult.error.message,
          });
        } catch {
          // ignore
        }
      } else {
        await dispatch(getPaymentCards());
        await dispatch(getCustomerProfile());
        dispatch(
          addToast({
            type: "success",
            message: messages.cardAdded,
          }),
        );
        await dispatch({ type: ACCOUNT_ADD_CARD_SUCCESS });

        try {
          await Tracker.track("Payment Method Added", { source: "Stripe" });
        } catch {
          // ignore
        }
        dispatch({ type: APP_LOADING_END });
        RootNavigation.navigate("paymentMethods");
        return true;
      }
    } catch (err) {
      console.error("error", err);
    }
    dispatch({ type: APP_LOADING_END });
    return true;
  };

export const getWallet =
  (withLoading = true): any =>
  async (dispatch: AppDispatch) => {
    if (withLoading) dispatch({ type: APP_LOADING_START });

    await apollo().cache.reset();

    try {
      const req: any = await apollo().query({
        query: gql`
          {
            sesameWalletValue
          }
        `,
      });

      dispatch({
        type: WALLET_AMOUNT_SET,
        wallet: req.data.sesameWalletValue,
      });
      if (withLoading) dispatch({ type: APP_LOADING_END });

      return true;
    } catch (err) {
      captureException(err);
      dispatch({ type: WALLET_AMOUNT_ERROR });
    }
    if (withLoading) dispatch({ type: APP_LOADING_END });
  };

export const checkWalletConsistencyWithBadge = (): any => async (dispatch: any, getState: any) => {
  dispatch({ type: APP_LOADING_START });
  const { account, auth } = getState();
  const shouldGenerateVirtualBadgeId =
    account.profile.companyEntity.hasQRCodeEnabled && !auth.user?.badgeId;

  if (!shouldGenerateVirtualBadgeId) {
    return;
  }

  try {
    const req = await apollo().mutate({
      mutation: gql`
        mutation sesameWalletOnboard {
          sesameWalletOnboard
        }
      `,
    });

    if (req.data?.sesameWalletOnboard) {
      // If badgeId has been re-generated, we refetch user and wallet for UI consistency
      batch(() => {
        dispatch(refreshUserFromToken());
        dispatch(getWallet());
      });
    }
    dispatch({ type: APP_LOADING_END });
  } catch (err) {
    dispatch({ type: APP_LOADING_END });
    captureException(err);
  }
};

export const getDefaultPaymentMethod = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: APP_LOADING_START });

  await apollo().cache.reset();

  try {
    const req: any = await apollo().query({
      query: gql`
        {
          sesameDefaultPaymentMethod {
            type
            id
            brand
            country
            customer
            cvc_check
            last4
            exp_month
            exp_year
          }
        }
      `,
    });

    dispatch({
      type: WALLET_DEFAULTPAYMENTMETHOD_SET,
      defaultPaymentMethod: req.data.sesameDefaultPaymentMethod,
    });
    dispatch({ type: APP_LOADING_END });
  } catch (err) {
    dispatch({ type: APP_LOADING_END });
    captureException(err);
    dispatch({ type: WALLET_DEFAULTPAYMENTMETHOD_ERROR });
  }
};

export const refillWalletByStripe =
  (stripeHoc: any): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getWalletMessages();
    dispatch({ type: APP_LOADING_START });
    dispatch({ type: WALLET_REFILL_LOADING });

    await dispatch(getWallet(false)); // sync wallet value

    const {
      account: { profile },
      form: {
        refill: { amountToRefill },
      },
      wallet,
    } = getState();

    // Check if user already have a stripeCustomerId
    if (!profile.stripeCustomerId) {
      await dispatch(createStripeCustomer());
    }

    let paymentIntentSecret = null;
    const maxRefillableAmount = getRefillPaymentLevel(
      3,
      profile.companyEntity.walletMaximumBalance,
      profile.companyEntity.walletMaximumAmountToRefillByBankCard,
      wallet.wallet ?? 0,
    );

    if (
      Number(amountToRefill) < config.business.minimumAmountToRefill ||
      Number(amountToRefill) > maxRefillableAmount
    ) {
      const incorrectAmountMessage = messages.incorrectAmount(
        renderCurrencyFromCentimes(config.business.minimumAmountToRefill),
        renderCurrencyFromCentimes(maxRefillableAmount),
      );
      dispatch(formHandleInput("refill", "amountToRefill", maxRefillableAmount));
      dispatch({ type: APP_LOADING_END });
      dispatch(
        addToast({
          type: "error",
          title: messages.oopsTitle,
          message: incorrectAmountMessage,
        }),
      );

      return dispatch({
        type: WALLET_REFILL_ERROR,
        errorMessage: incorrectAmountMessage,
      });
    }

    try {
      const req: any = await apollo().mutate<any>({
        variables: {
          amountToRefill: Number(amountToRefill),
        },
        mutation: gql`
          mutation createSesamePaymentIntent($amountToRefill: Int!) {
            createSesamePaymentIntent(amountToRefill: $amountToRefill)
          }
        `,
      });

      if (!req.data.createSesamePaymentIntent) {
        throw new Error("Unable to create SesamePaymentIntent");
      } else {
        paymentIntentSecret = req.data.createSesamePaymentIntent;
      }
    } catch (err) {
      dispatch({ type: APP_LOADING_END });
      dispatch(
        addToast({
          type: "error",
          message: messages.technicalError,
        }),
      );
      captureException(err);
      dispatch({
        type: WALLET_REFILL_ERROR,
        errorMessage: messages.technicalError,
      });
    }

    let data: any = {};

    data.payment_method = (wallet.defaultPaymentMethod as any).id;

    const result = await stripeHoc.handleCardPayment(paymentIntentSecret, data);
    if (!result.error && result.paymentIntent.status.toLowerCase() === "succeeded") {
      await sleep(4000);
      await dispatch(getWallet(false));
      dispatch({ type: WALLET_REFILL_SUCCESS });
      dispatch(
        addToast({
          type: "success",
          title: messages.successTitle,
          message: messages.walletCredited,
        }),
      );
      dispatch({ type: APP_LOADING_END });
      RootNavigation.navigate("profile");

      return true;
    } else {
      dispatch({ type: APP_LOADING_END });
      dispatch(
        addToast({
          type: "error",
          message: result.error.message ?? messages.technicalError,
        }),
      );
      await dispatch({
        type: WALLET_REFILL_ERROR,
        errorMessage: result.error.message ?? messages.technicalError,
      });

      return false;
    }
  };

export const refillWalletByEdenred =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getWalletMessages();
    dispatch({ type: WALLET_REFILL_LOADING });
    dispatch({ type: APP_LOADING_START });

    await dispatch(getWallet(false)); // sync wallet value

    const {
      account: { profile },
      form: {
        refill: { amountToRefill },
      },
      wallet,
    } = getState();

    const maxRefillableAmount = getRefillPaymentLevel(
      3,
      profile.companyEntity.walletMaximumBalance,
      profile.companyEntity.walletMaximumAmountToRefillByMealVoucher,
      wallet.wallet ?? 0,
    );

    if (
      Number(amountToRefill) < config.business.minimumAmountToRefill ||
      Number(amountToRefill) > maxRefillableAmount
    ) {
      const incorrectAmountMessage = messages.incorrectAmount(
        renderCurrencyFromCentimes(config.business.minimumAmountToRefill),
        renderCurrencyFromCentimes(maxRefillableAmount),
      );
      dispatch(formHandleInput("refill", "amountToRefill", maxRefillableAmount));
      dispatch({ type: APP_LOADING_END });
      return dispatch(
        addToast({
          type: "error",
          title: messages.oopsTitle,
          message: incorrectAmountMessage,
        }),
      );
    }

    try {
      const req: any = await apollo().mutate<any>({
        variables: {
          amountToRefill: Number(amountToRefill),
        },
        mutation: gql`
          mutation refillWalletByEdenred($amountToRefill: Int!) {
            refillWalletByEdenred(amountToRefill: $amountToRefill)
          }
        `,
      });

      if (req.errors && req.errors[0] && req.errors[0].message) {
        dispatch({ type: APP_LOADING_END });
        dispatch(
          addToast({
            type: "error",
            title: messages.oopsTitle,
            message: String(req.errors[0].message) ?? messages.technicalError,
          }),
        );
        dispatch({
          type: WALLET_REFILL_ERROR,
          errorMessage: String(req.errors[0].message) ?? messages.technicalError,
        });
      }

      if (req.data.refillWalletByEdenred) {
        batch(() => {
          dispatch(getWallet(false));
          dispatch({ type: WALLET_REFILL_SUCCESS });
          dispatch(
            addToast({
              type: "success",
              message: messages.walletCredited,
            }),
          );
          dispatch({ type: APP_LOADING_END });
          RootNavigation.navigate("profile");
        });
      }
    } catch (err) {
      dispatch({ type: APP_LOADING_END });
      captureException(err);
    }
  };

export const refillWalletByPayzen =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const messages = getWalletMessages();
    await dispatch(resetPayzenFormToken());

    await dispatch(getWallet()); // sync wallet value
    dispatch({ type: APP_LOADING_START });

    const {
      form: {
        refill: { amountToRefill },
      },
      wallet,
    } = getState();

    try {
      const req: any = await apollo().mutate<any>({
        variables: {
          amountToRefill: Number(amountToRefill),
        },
        mutation: gql`
          mutation refillWalletByPayzen($amountToRefill: Int!) {
            refillWalletByPayzen(amountToRefill: $amountToRefill)
          }
        `,
      });

      if (req.data.refillWalletByPayzen) {
        //batch(() => {
        if (req.data.refillWalletByPayzen === "true") {
          // payzen formAction SILENT
          dispatch({ type: WALLET_REFILL_SUCCESS });
          await dispatch(getWallet());
          dispatch(
            addToast({
              type: "success",
              message: messages.walletCredited,
            }),
          );
          RootNavigation.navigate("home");
        } else {
          // payzen formAction PAYMENT
          dispatch({
            type: WALLET_PAYZENFORMTOKEN_SET,
            payzenFormToken: req.data.refillWalletByPayzen,
          });
        }
        // dispatch(getWallet());
        // dispatch({ type: WALLET_REFILL_SUCCESS });
        //});
      } else {
        dispatch(
          addToast({
            type: "error",
            message: messages.technicalError,
          }),
        );
      }
    } catch (err) {
      captureException(err);
      dispatch(
        addToast({
          type: "error",
          message: messages.payzenDailyLimit || messages.technicalError,
        }),
      );
    }
    dispatch({ type: APP_LOADING_END });
  };

export const walletOnboardingByEdenred =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: WALLET_ONBOARDING_LOADING });
    const messages = getWalletMessages();

    const {
      account: { profile },
      form: {
        refill: { amountToRefill },
      },
    } = getState();

    const maximumAmountToRefill = profile.companyEntity.walletMaximumAmountToRefillByMealVoucher;

    if (
      Number(amountToRefill) < config.business.minimumAmountToRefill ||
      Number(amountToRefill) > maximumAmountToRefill
    ) {
      const incorrectAmountMessage = messages.incorrectAmount(
        renderCurrencyFromCentimes(config.business.minimumAmountToRefill),
        renderCurrencyFromCentimes(maximumAmountToRefill),
      );
      return dispatch(
        addToast({
          type: "error",
          title: messages.oopsTitle,
          message: incorrectAmountMessage,
        }),
      );
    }

    try {
      const req: any = await apollo().mutate<any>({
        variables: {
          amountToRefill: Number(amountToRefill),
        },
        mutation: gql`
          mutation refillWalletByEdenred($amountToRefill: Int!) {
            refillWalletByEdenred(amountToRefill: $amountToRefill)
          }
        `,
      });

      if (req.errors && req.errors[0] && req.errors[0].message) {
        dispatch(
          addToast({
            type: "error",
            message: String(req.errors[0].message),
          }),
        );
      }

      if (req.data.refillWalletByEdenred) {
        await dispatch(getWallet());
        dispatch(
          addToast({
            type: "success",
            message: messages.walletCredited,
          }),
        );
        RootNavigation.navigate("home");
      } else {
        dispatch(
          addToast({
            type: "error",
            message: messages.technicalError,
          }),
        );
      }
    } catch (err) {
      captureException(err);
    }
  };

export const walletOnboardingByStripe =
  (confirmPayment: any): any =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch({ type: APP_LOADING_START });
    const messages = getWalletMessages();

    const {
      account: { profile },
      form: {
        refill: { amountToRefill },
      },
    } = getState();

    // Check if user already have a stripeCustomerId
    if (!profile.stripeCustomerId) {
      await dispatch(createStripeCustomer());
    }

    let paymentIntentSecret = null;
    const maximumAmountToRefill = profile.companyEntity.walletMaximumAmountToRefillByBankCard;

    if (
      Number(amountToRefill) < config.business.minimumAmountToRefill ||
      Number(amountToRefill) > maximumAmountToRefill
    ) {
      const errorMessage = messages.incorrectAmount(
        renderCurrencyFromCentimes(config.business.minimumAmountToRefill),
        renderCurrencyFromCentimes(maximumAmountToRefill),
      );
      dispatch({
        type: WALLET_REFILL_ERROR,
        errorMessage,
      });
      return dispatch(
        addToast({
          type: "error",
          message: errorMessage,
        }),
      );
    }

    try {
      const req: any = await apollo().mutate<any>({
        variables: {
          amountToRefill: Number(amountToRefill),
        },
        mutation: gql`
          mutation createSesamePaymentIntent($amountToRefill: Int!) {
            createSesamePaymentIntent(amountToRefill: $amountToRefill)
          }
        `,
      });

      if (!req.data.createSesamePaymentIntent) {
        dispatch(
          addToast({
            type: "error",
            message: messages.technicalError,
          }),
        );
        dispatch({ type: APP_LOADING_END });
        throw new Error("Unable to create SesamePaymentIntent");
      } else {
        paymentIntentSecret = req.data.createSesamePaymentIntent;
      }
    } catch (err: any) {
      captureException(err);
      dispatch({ type: APP_LOADING_END });
      dispatch(
        addToast({
          type: "error",
          message:
            err?.message && messages.technicalError
              ? `${messages.technicalError}: ${String(err.message)}`
              : messages.technicalError,
        }),
      );
      dispatch({
        type: WALLET_ONBOARDING_ERROR,
        errorMessage: messages.technicalError,
      });
    }

    const data: any = {
      paymentMethodType: "Card",
      paymentMethodData: {
        save_payment_method: true,
      },
    };
    try {
      const result = await confirmPayment(paymentIntentSecret, data);
      if (!result.error && result.paymentIntent.status === "Succeeded") {
        await sleep(4000);
        await dispatch({ type: WALLET_ONBOARDING_SUCCESS });
        await dispatch(getWallet());
        await dispatch(getCustomerProfile(false));
        await dispatch(
          addToast({
            type: "success",
            message: messages.walletCredited,
          }),
        );

        RootNavigation.navigate("home");
      } else {
        dispatch({
          type: WALLET_ONBOARDING_ERROR,
          errorMessage: result.error.message,
        });
      }
    } catch (err) {
      console.error("error", err);
      captureException(err);
    }
    dispatch({ type: APP_LOADING_END });
  };

export const resetRefillState = (): any => async (dispatch: AppDispatch) => {
  dispatch({ type: WALLET_REFILL_RESET });
};

export const handleEdenredAuthCallback =
  (authorizationCode: string, state: string, sessionState: string, redirectRoute?: string): any =>
  async (dispatch: AppDispatch) => {
    const messages = getWalletMessages();
    dispatch({ type: APP_LOADING_START });
    if (
      !authorizationCode ||
      !state ||
      !sessionState ||
      authorizationCode.length === 0 ||
      state.length === 0 ||
      sessionState.length === 0
    ) {
      dispatch(
        addToast({
          type: "error",
          message: messages.edenred.communicationError,
        }),
      );
    }

    await apollo().cache.reset();
    const storageAuthorizationCode = await CookieManager.get(config.cookies.url)
      .then((cookies: any) => {
        return cookies.edenred_authorization_code?.value as string | undefined;
      })
      .catch((err: any) => {
        captureException(err);
        return undefined;
      });

    if (storageAuthorizationCode === authorizationCode) {
      return;
    } else {
      await CookieManager.set(config.cookies.url, {
        name: "edenred_authorization_code",
        value: authorizationCode,
        path: "/",
        version: "1",
      });
    }

    try {
      const req: any = await apollo().mutate<any>({
        variables: {
          authorizationCode,
        },
        mutation: gql`
          mutation refreshEdenredToken($authorizationCode: String!) {
            refreshEdenredToken(authorizationCode: $authorizationCode)
          }
        `,
      });

      if (!req.data.refreshEdenredToken) {
        dispatch(
          addToast({
            type: "error",
            message: messages.edenred.linkError,
          }),
        );

        try {
          // await Tracker.track("Payment Method Added Failed", {
          //   source: "Edenred",
          //   error: "unknown",
          // });
        } catch {
          // ignore
        }
      } else {
        dispatch({ type: WALLET_EDENREDAUTHCALLBACK_SUCCESS, page: state });
        dispatch(
          addToast({
            type: "success",
            message: messages.edenred.linked,
          }),
        );
        await dispatch(getEdenredUsername());
        RootNavigation.navigate(redirectRoute || "onboardingRefillModel", {
          defaultRefillMethod: "edenred",
        });

        try {
          // await Tracker.track("Payment Method Added", { source: "Edenred" });
        } catch {
          // ignore
        }
      }
    } catch (err) {
      captureException(err);
      dispatch(
        addToast({
          type: "error",
          message: messages.edenred.linkError,
        }),
      );

      try {
        // await Tracker.track("Payment Method Added Failed", {
        //   source: "Edenred",
        //   error: "unknown",
        // });
      } catch {
        // ignore
      }
    }
    dispatch({ type: APP_LOADING_END });

    return true;
  };

export const getPayzenFormToken =
  (amount: number): any =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: WALLET_PAYZENFORMTOKEN_LOADING });
    dispatch({ type: APP_LOADING_START });

    try {
      const req: any = await apollo().mutate<any>({
        variables: {
          amount,
        },
        mutation: gql`
          mutation generatePayzenFormTokenForCanteen($amount: Int!) {
            generatePayzenFormTokenForCanteen(amount: $amount)
          }
        `,
      });

      if (req.data.generatePayzenFormTokenForCanteen) {
        dispatch({
          type: WALLET_PAYZENFORMTOKEN_SET,
          payzenFormToken: req.data.generatePayzenFormTokenForCanteen,
        });
      }
      dispatch({ type: APP_LOADING_END });
    } catch (err) {
      dispatch({ type: APP_LOADING_END });
      captureException(err);
      dispatch({
        type: WALLET_PAYZENFORMTOKEN_SET,
        payzenFormToken: "",
      });
    }
  };
export const setWalletRefillSuccess =
  (): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    const { wallet } = getState();

    if (!wallet.refillSuccess) {
      await sleep(4000);
      await dispatch(getWallet());
      await dispatch({ type: WALLET_REFILL_SUCCESS });
    }
  };

export const resetPayzenFormToken = (): any => async (dispatch: AppDispatch) => {
  dispatch({
    type: WALLET_PAYZENFORMTOKEN_SET,
    payzenFormToken: "",
  });
};
